import type { AppDispatch, RootState } from '@/store/store'
import type { FolderResponse } from '@/user_files/folders/schemas'
import { selectCurrentFolderId } from '@/user_files/folders/store/selectors'
import { UserFoldersActions } from '@/user_files/folders/store/slice'

/**
 * Caches folders a mutation has just learned about, everywhere their position cannot be seen wrong.
 *
 * The drive renders `childrenByParentId` in the order it holds, and a cached row can only go on the
 * end of it — so on the folder the drive is displaying, caching shows the folder at the bottom until
 * the listing that places it arrives, and then moves it. Callers there dispatch `fetchFolderChildren`
 * instead and let that listing introduce the row, already in the sort the user picked.
 *
 * Everywhere else the row is wanted straight away and there is nothing to fetch it: the file picker
 * and the move dialog read through `selectChildFolders`, which sorts by name itself, and they
 * refetch only when their own browse folder changes. Upserting appends and never removes, so it
 * cannot drop a sibling a concurrent caller added.
 */
export function cacheResolvedFolders(params: { dispatch: AppDispatch; getState: () => RootState; parentId: string }, folders: FolderResponse[]): void {
  if (selectCurrentFolderId(params.getState()) === params.parentId) {
    // Records but not rows. The listing fetch owns membership and order here, but the records are
    // still needed: the move dialog navigates straight into a folder it has just created, and a
    // folder with no record cannot hold the loading state for its own listing.
    params.dispatch(UserFoldersActions.upsertFolderRecords(folders))
    return
  }

  params.dispatch(UserFoldersActions.upsertFolders(folders))
}
